import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import './AuthPage.css';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<'verify' | 'reset'>('verify');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verifySuccess, setVerifySuccess] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Get email from location state or prompt user
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    }
  }, [location]);

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await authService.verifyResetCode(email, code);
      setVerifySuccess(true);
      setSuccess(t('auth.codeSentSuccess')); // Tạm dùng key này hoặc hardcoded text "Mã hợp lệ" nếu cần
      setStep('reset');
      // Clear success after a moment so form is not disabled
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.fail'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('auth.passwordLengthError') || 'Mật khẩu phải có ít nhất 6 ký tự.');
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(email, code, newPassword);
      setResetSuccess(true);
      setSuccess(t('auth.resetSuccess'));
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth.fail'));
    } finally {
      setLoading(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="auth-page">
        <div className="auth-wrapper">
          <div className="auth-illustration">
            <div className="auth-illustration-content">
              <div className="auth-illustration-icon">🔑</div>
              <h1>{t('auth.verifyResetCode')}</h1>
              <p>
                {t('auth.resetPasswordDesc')}
              </p>
            </div>
          </div>

          <div className="auth-container">
            <div className="auth-header">
              <h2>{t('auth.enterCode')}</h2>
              <p>{t('auth.enterCodeDesc')}</p>
            </div>

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <form onSubmit={handleVerifyCode}>
              <div className="form-group">
                <label htmlFor="email">{t('auth.email')}</label>
                <div className={`input-wrapper ${email ? 'has-value' : ''}`}>
                  <span className="input-icon">📧</span>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.emailPlaceholder')}
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="code">{t('auth.enterCode')}</label>
                <div className={`input-wrapper ${code ? 'has-value' : ''}`}>
                  <span className="input-icon">🔢</span>
                  <input
                    type="text"
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    required
                    disabled={loading}
                    style={{ letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.2em' }}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="auth-button"
                disabled={loading || code.length !== 6}
              >
                {loading ? t('auth.verifying') : t('auth.verifyButton')}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                {t('common.notReceived') || 'Chưa nhận được mã?'} <Link to="/forgot-password">{t('auth.resendCode')}</Link>
              </p>
              <p>
                {t('auth.rememberPassword')} <Link to="/login">{t('auth.loginButton')}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        <div className="auth-illustration">
          <div className="auth-illustration-content">
            <div className="auth-illustration-icon">🔐</div>
            <h1>{t('auth.resetPasswordTitle')}</h1>
            <p>
              {t('auth.resetPasswordDesc')}
            </p>
          </div>
        </div>

        <div className="auth-container">
          <div className="auth-header">
            <h2>{t('auth.resetPasswordTitle')}</h2>
            <p>{t('auth.resetPasswordDesc')}</p>
          </div>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message" role="alert">
              {success}
            </div>
          )}

          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label htmlFor="newPassword">{t('auth.newPassword')}</label>
              <div className={`input-wrapper ${newPassword ? 'has-value' : ''}`}>
                <span className="input-icon">🔒</span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('auth.newPasswordPlaceholder')}
                  required
                  minLength={6}
                  title={t('auth.confirmPasswordPlaceholder')}
                  onInvalid={(e) => {
                    const target = e.target as HTMLInputElement;
                    if (target.validity.valueMissing) {
                      target.setCustomValidity(t('common.validation.required'));
                    } else if (target.validity.tooShort) {
                      target.setCustomValidity(t('common.validation.minLength', { count: target.value.length }));
                    }
                  }}
                  onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                  disabled={loading || resetSuccess}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
              <div className={`input-wrapper ${confirmPassword ? 'has-value' : ''}`}>
                <span className="input-icon">🔒</span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  required
                  disabled={loading || resetSuccess}
                  minLength={6}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading || resetSuccess}
            >
              {loading ? t('auth.resetting') : resetSuccess ? t('common.success') || 'Thành công!' : t('auth.resetButton')}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {t('auth.rememberPassword')} <Link to="/login">{t('auth.loginButton')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;

